import { createContext, useContext } from 'react'
import { useQuery } from 'react-query'

import { useAuthentication } from '@hooks/useAuthentication'
import { StoryContextType } from './interface'
import { useBookAction } from './useBookAction'

interface StoryProviderType {
  children: React.ReactNode
  bookId: number
}

export const StoryContext = createContext<StoryContextType>({
  data: undefined,
  charactersData: undefined,
  isLoading: false,
  isCharactersLoading: false,
  refetchStory: async () => {},
  refetchCharacters: async () => {},
  bookId: 0,
})

export function StoryProvider({ children, bookId }: StoryProviderType) {
  const { isAuthenticated } = useAuthentication()
  const { getStoryDetail, getCharacters } = useBookAction()
  const {
    data,
    isLoading,
    refetch: refetchStory,
  } = useQuery(['story-detail', bookId], () => getStoryDetail(bookId), {
    enabled: !!bookId && isAuthenticated,
  })

  const {
    data: charactersData,
    isLoading: isCharactersLoading,
    refetch: refetchCharacters,
  } = useQuery(['characters', bookId], () => getCharacters(bookId), {
    enabled: !!bookId && isAuthenticated,
  })

  const value = {
    data,
    charactersData,
    isLoading,
    isCharactersLoading,
    refetchStory,
    refetchCharacters,
    bookId,
  }

  return <StoryContext.Provider value={value}>{children}</StoryContext.Provider>
}

export function useStory() {
  return useContext(StoryContext)
}
